import React, {useState} from 'react';

import {Button, Icon, IconButton, Text} from '@albert-io/atomic';
import type {IconProp, IconStyle} from '@albert-io/atomic/atoms/Icon/Icon.react';

import {history} from 'client/history';
import {ClassworkRow} from 'client/TeacherDashboard/components/ClassworkRow';
import {ErrorRetryState} from 'client/TeacherDashboard/components/ErrorRetryState';
import {
  SkillPathListGroup,
  SkillPathListGroupEmpty
} from 'client/TeacherDashboard/SkillPaths/SkillPathListGroup';
import {formatDateRange, formatDateTime, getDueCopy} from 'client/TeacherDashboard/rowDateCopy';
import {AdaptiveOnboardingEmptyState} from 'client/TeacherDashboard/components/AdaptiveEmptyState';
import {STATUS_SEGMENTS} from 'client/TeacherDashboard/SkillPaths/constants';
import type {SkillPathStatusSegment} from 'client/TeacherDashboard/SkillPaths/types';

import type {DiagnosticStatus, DiagnosticStatusKind} from '../Management/lib/diagnosticStatus';
import {diagnosticStatus} from '../Management/lib/diagnosticStatus';
import type {TeacherDiagnosticListFieldsFragment} from '../Management/queries/TeacherDiagnostics.queries.generated';

/** Rows shown before the teacher expands the group, per the design. */
const COLLAPSED_ROWS = 3;

const STATUS_DISPLAY: Record<
  DiagnosticStatusKind,
  {color: 'tertiary' | 'warning'; icon?: IconProp; iconStyle?: IconStyle}
> = {
  open: {color: 'tertiary'},
  scheduled: {color: 'warning', icon: 'clock', iconStyle: 'light'},
  closed: {color: 'tertiary', icon: 'ban', iconStyle: 'regular'}
};

const getSubtitleSections = (status: DiagnosticStatus, showExactDueDate: boolean): string[] => {
  if (status.kind === 'scheduled') {
    return [
      status.from
        ? `Starts ${formatDateRange(status.from, status.to, formatDateTime)}`
        : 'Scheduled'
    ];
  }
  const dueCopy = getDueCopy({minDueDate: status.from, maxDueDate: status.to}, showExactDueDate);
  if (status.kind === 'closed') {
    return dueCopy ? ['Closed', dueCopy] : ['Closed'];
  }
  return [dueCopy || 'Open'];
};

const editPath = (diagnostic: TeacherDiagnosticListFieldsFragment): string =>
  `/diagnostics/${diagnostic.diagnosticTemplateId}/assign/${diagnostic.id}`;

const DiagnosticRow = ({diagnostic}: {diagnostic: TeacherDiagnosticListFieldsFragment}) => {
  const status = diagnosticStatus(diagnostic);
  const {color, icon, iconStyle} = STATUS_DISPLAY[status.kind];
  const goToEdit = () => history.push(editPath(diagnostic));

  return (
    <ClassworkRow
      actions={
        <>
          <IconButton
            disabled
            icon='chart-line'
            iconStyle='regular'
            label='View Report'
            tooltip='View Report'
            size='s'
          />
          <IconButton
            icon='pencil'
            iconStyle='regular'
            label='Edit Diagnostic'
            tooltip='Edit Diagnostic'
            size='s'
            onClick={goToEdit}
          />
        </>
      }
      data-testid='diagnostic-row'
      icon={['fal', 'file-contract']}
      iconStyle='light'
      renderSubtitle={(isHovered) => (
        <Text as='div' className='truncate' color={color} size='s'>
          {icon ? (
            <Icon className='mr-x0-half text-xs!' color={color} icon={icon} iconStyle={iconStyle} />
          ) : null}
          {getSubtitleSections(status, isHovered).map((part, index) => (
            <React.Fragment key={part}>
              {index > 0 && <span className='mx-x1'>•</span>}
              {part}
            </React.Fragment>
          ))}
        </Text>
      )}
      title={
        <Text className='content-primary truncate' size='m'>
          {diagnostic.name}
        </Text>
      }
      onOpen={goToEdit}
    />
  );
};

interface Props {
  diagnostics: TeacherDiagnosticListFieldsFragment[];
  /** Total matching the status + filters, shown beside the group title. */
  total: number;
  segment: SkillPathStatusSegment;
  /** Every status is empty under the current filters — show the pitch, not "no open …". */
  isEmptyInEveryStatus: boolean;
  classroomId?: string;
  hasError: boolean;
  onRetry: () => void;
}

export const DiagnosticsGroup = ({
  diagnostics,
  total,
  segment,
  isEmptyInEveryStatus,
  classroomId,
  hasError,
  onRetry
}: Props) => {
  const [isExpanded, setIsExpanded] = useState(false);
  // A cache-served tab or filter change keeps this mounted, so the expansion has to be reset here.
  const [renderedDiagnostics, setRenderedDiagnostics] = useState(diagnostics);
  if (renderedDiagnostics !== diagnostics) {
    setRenderedDiagnostics(diagnostics);
    setIsExpanded(false);
  }

  const visible = isExpanded ? diagnostics : diagnostics.slice(0, COLLAPSED_ROWS);
  // Counted off the loaded page, not `total`: expanding only reveals rows already in hand, so sizing
  // this off the server total would leave a button that reveals nothing once the page cap is hit.
  const remaining = diagnostics.length - visible.length;
  const statusLabel = STATUS_SEGMENTS.find((s) => s.value === segment)?.label.toLowerCase() ?? '';

  return (
    <SkillPathListGroup count={total} title='Diagnostics'>
      {hasError ? (
        <ErrorRetryState copy='We couldn’t load your diagnostics.' onRetry={onRetry} />
      ) : diagnostics.length === 0 ? (
        <SkillPathListGroupEmpty inset={isEmptyInEveryStatus ? 'panel' : 'text'}>
          {isEmptyInEveryStatus ? (
            <AdaptiveOnboardingEmptyState
              classroomId={classroomId}
              layout='inline'
              variant='diagnostics'
            />
          ) : (
            <Text color='secondary'>No {statusLabel} diagnostics</Text>
          )}
        </SkillPathListGroupEmpty>
      ) : (
        <>
          <ul className='m-x0 p-x0 flex list-none flex-col'>
            {visible.map((diagnostic) => (
              <li key={diagnostic.id}>
                <DiagnosticRow diagnostic={diagnostic} />
              </li>
            ))}
          </ul>
          {remaining > 0 && (
            <div className='pl-x8'>
              <Button
                color='secondary'
                roundness='pill'
                size='s'
                variant='outlined'
                onClick={() => setIsExpanded(true)}
              >
                Show {remaining} More
              </Button>
            </div>
          )}
        </>
      )}
    </SkillPathListGroup>
  );
};
